import React, { useRef, useEffect, useState } from 'react'

import classNames from 'classnames'
import styled from 'styled-components'
import { ArrowUpOutlined, DeploymentUnitOutlined, FileAddOutlined, PaperClipOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { Button, Dropdown, Tooltip } from 'antd'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { ROUTES } from 'shared/constants/routesPaths'
import { uploadAiAssistantFilesThunk, UPLOADER_LIST_TYPES } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { getUploadingFilesSelector } from 'features/files-uploading/selectors/filesUploadingSelectors'
import { userSettingByPathSelector } from 'features/user/model/selectors/userSelectors'
import { t } from 'i18next'

import { selectDraftMessage, selectSystemHints } from '../model/selectors/aiAssistantSelectors'
import { sendMessageThunk, aiAssistantSlice, addAttachmentsFromUploadedFilesThunk, setUseAssetsThunk } from '../model/slice/aiAssistantSlice'

import { WriterAttachments } from './WriterAttachments'

interface AiMessageWriterProps {
  placeholder?: string
  onSend?: (message: string) => void
  disabled?: boolean
  btnDisabled?: boolean
  className?: string
}

export const AiMessageWriter: React.FC<AiMessageWriterProps> = ({
  placeholder = 'Enter the command',
  onSend,
  disabled = false,
  btnDisabled = false,
  className,
}) => {
  const contentEditableRef = useRef<HTMLDivElement>(null)
  const { chatId } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const systemHints = useAppSelector(selectSystemHints)
  const useAssets = systemHints.includes('use_documents')

  const [messageLoading, setMessageLoading] = useState(false)
  const getDraftMessage = useAppSelector(selectDraftMessage)
  const fsSettings = useAppSelector((state) => userSettingByPathSelector(state, 'dam.fs'))
  const draftMessage = getDraftMessage(chatId || 'new')
  const [isFocused, setIsFocused] = useState(false)
  const [hasText, setHasText] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const userId = useAppSelector(state => state.user.user?.family_user_id)
  const uploadingFiles = useAppSelector(state => state.filesUploading)
  const attachments = useAppSelector(state => state.aiAssistant.attachments)
  const uploadingAiItems = getUploadingFilesSelector().filter((f: any) => f.uploadingSourceType === UPLOADER_LIST_TYPES.AI_ASSISTANT_ATTACHMENTS)
  const totalAttachmentsCount = (attachments?.length || 0) + (uploadingAiItems?.length || 0)
  const isAttachmentsLimitReached = totalAttachmentsCount >= 6

  useEffect(() => {
    if (contentEditableRef.current) {
      contentEditableRef.current.focus()
      const textContent = contentEditableRef.current?.textContent || ''
      setHasText(textContent.trim().length > 0)
    }
  }, [])

  const adjustHeight = () => {
    if (contentEditableRef.current) {
      contentEditableRef.current.style.height = 'auto'
      contentEditableRef.current.style.height = `${contentEditableRef.current.scrollHeight}px`
    }
  }

  useEffect(() => {
    adjustHeight()
  }, [draftMessage])

  useEffect(() => {
    if (contentEditableRef.current && contentEditableRef.current.innerHTML !== draftMessage) {
      contentEditableRef.current.innerHTML = draftMessage
      adjustHeight()
      const textContent = contentEditableRef.current?.textContent || ''
      setHasText(textContent.trim().length > 0)
    }
  }, [draftMessage])

  useEffect(() => {
    if (!userId) return

    const completedFiles = uploadingFiles.entities
      ? Object.values(uploadingFiles.entities).filter(
        (file: any) => file.uploadingSourceType === UPLOADER_LIST_TYPES.AI_ASSISTANT_ATTACHMENTS && file.state.isCompleted && file.uploadId && !attachments.some(a => a.id === file.uploadId)
      )
      : []

    if (completedFiles.length > 0) {
      dispatch(addAttachmentsFromUploadedFilesThunk({ files: completedFiles }))
    }
  }, [uploadingFiles, userId, attachments, dispatch])

  const handleSubmit = async () => {
    if (draftMessage.trim() && !messageLoading && !disabled) {
      if (onSend) {
        onSend(draftMessage.trim())
      } else {
        setMessageLoading(true)

        try {
          const result = await dispatch(sendMessageThunk({
            message: draftMessage.trim(),
            chatId: chatId
          }))

          if (sendMessageThunk.fulfilled.match(result)) {
            const newChatId = result.payload.chatId
            if (!chatId && newChatId) {
              navigate(`${ROUTES.dashboard.aiAssistant}/${newChatId}`)
            }
          }
        } finally {
          setMessageLoading(false)
        }
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!messageLoading && !disabled) {
        handleSubmit()
      }
    }
  }

  const handleChange = () => {
    const currentChatId = chatId || 'new'
    const value = contentEditableRef.current?.innerHTML || ''
    const textContent = contentEditableRef.current?.textContent || ''
    const hasTextContent = textContent.trim().length > 0

    setHasText(hasTextContent)

    dispatch(aiAssistantSlice.actions.setDraftMessage({
      chatId: currentChatId,
      message: hasTextContent ? value : ''
    }))
  }

  const handleOpenAttachmentsModal = () => {
    dispatch(aiAssistantSlice.actions.setAttachmentsModalOpen(true))
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (files && files.length > 0) {
      const remainingSlots = Math.max(0, 6 - totalAttachmentsCount)
      if (remainingSlots <= 0) {
        e.target.value = ''
        return
      }
      const dt = new DataTransfer()
      Array.from(files).slice(0, remainingSlots).forEach(file => dt.items.add(file))
      const limitedFiles = dt.files

      dispatch(uploadAiAssistantFilesThunk({
        files: limitedFiles,
        symlinkParentId: fsSettings?.value?.attachments_dir_id,
        uploadingSourceType: UPLOADER_LIST_TYPES.AI_ASSISTANT_ATTACHMENTS
      }))

      e.target.value = ''
    }
  }

  const handleDropdownClick = (info: any) => {
    if (isAttachmentsLimitReached) return
    if (info.key === 'upload') {
      fileInputRef.current?.click()
    } else if (info.key === 'assets') {
      handleOpenAttachmentsModal()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  const handleUseAssetsChange = (useAssets: boolean) => {
    if (!chatId) {
      dispatch(aiAssistantSlice.actions.setSystemHints(useAssets ? ['use_documents'] : []))

      return
    }

    dispatch(setUseAssetsThunk({ chatId: chatId, useAssets }))
  }

  const dropdownItems = [
    {
      key: 'upload',
      label: 'Add from device',
      icon: <PlusCircleOutlined />
    },
    {
      key: 'assets',
      label: 'Add from assets',
      icon: <FileAddOutlined />,
      onClick: handleOpenAttachmentsModal
    },
  ]

  return (
    <SContainer className={classNames(className, { focused: isFocused })}>
      <WriterAttachments />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <SContentEditable
        ref={contentEditableRef}
        contentEditable={!disabled}
        className={classNames({ placeholder: !hasText && !isFocused })}
        suppressContentEditableWarning
        onInput={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPaste={handlePaste}
        disabled={disabled}
        aria-disabled={disabled}
        spellCheck={true}
        tabIndex={0}
        role="textbox"
        data-placeholder={placeholder}
      >
        {(!hasText && !isFocused) ? placeholder : ''}
      </SContentEditable>

      <SBottomContent>
        <Tooltip
          title={t('l_common_useAssetsInfo')}
          placement='bottom'
        >
          <SUseAssetsButton
            className={classNames({ active: useAssets })}
            onClick={() => handleUseAssetsChange(!useAssets)}>
            <SUseAssetsButtonIcon />

            {t('a_common_useAssets')}
          </SUseAssetsButton>
        </Tooltip>

        <Dropdown
          placement="topLeft"
          trigger={isAttachmentsLimitReached ? [] : ['click']}
          open={isAttachmentsLimitReached ? false : undefined}
          menu={{
            items: dropdownItems,
            onClick: handleDropdownClick,
          }}
        >
          <SAttachmentsButton
            className={classNames({ disabled: disabled || messageLoading || isAttachmentsLimitReached })}
          />
        </Dropdown>

        <SSendButton
          shape="circle"
          icon={<ArrowUpOutlined />}
          type="primary"
          onClick={handleSubmit}
          disabled={!hasText || disabled || messageLoading || btnDisabled || uploadingAiItems.length > 0}
          size="small"
        />
      </SBottomContent>
    </SContainer>
  )
}

const SContainer = styled.div`
  border: 1px solid #F0F0F0;
  background-color: #F5F5F5;
  border-radius: 16px;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2;
  padding-bottom: 40px;
  box-sizing: border-box;
  min-height: 104px;
`

const SContentEditable = styled.div<{
  disabled?: boolean
}>`
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  max-height: 500px;
  padding: 16px 16px 0;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #000000E0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &.placeholder {
    color: #00000073;
  }
`

const SBottomContent = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  width: calc(100% - 32px);
  gap: 8px;
  box-sizing: border-box;
`

const SSendButton = styled(Button)`
  &&& {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    
    &:hover {
      background-color: var(--color-primary);
      border-color: var(--color-primary);
    }

    &:disabled {
      background-color: #00000026;
      border-color: #0000001A;
      border: none;
      color: #FFFFFF;
    }
  }
`
const SAttachmentsButton = styled(PaperClipOutlined)`
  margin-left: auto;
  width: 24px;
  height: 24px;
  color: #00000073;
  cursor: pointer;
  font-size: 18px;
  outline: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SUseAssetsButton = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  color: #000000E0;
  border: 1px solid #D9D9D9;
  border-radius: 6px;

  &.active {
    background-color: #F8F0FF;
    border-color: #A871EB;
    color: #A871EB;
  }
`

const SUseAssetsButtonIcon = styled(DeploymentUnitOutlined)`
  font-size: 16px;
`